"use client";

import { c as _c } from "react/compiler-runtime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/free-regular-svg-icons";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import CommonCalendar from "@/components/utils/calendar/CommonCalendar";
import type { CreateWaveDatesConfig } from "@/types/waves.types";
import { CREATE_WAVE_START_DATE_LABELS } from "@/helpers/waves/waves.constants";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { Time } from "@/helpers/time";
import DateAccordion from "@/components/common/DateAccordion";
import TooltipIconButton from "@/components/common/TooltipIconButton";
interface StartDatesProps {
  readonly waveType: ApiWaveType;
  readonly dates: CreateWaveDatesConfig;
  readonly setDates: (dates: CreateWaveDatesConfig) => void;
  readonly isExpanded: boolean;
  readonly setIsExpanded: (expanded: boolean) => void;
}
export default function StartDates(t0) {
  const $ = _c(50);
  const {
    waveType,
    dates,
    setDates,
    isExpanded,
    setIsExpanded
  } = t0;
  const isRankWave = waveType === ApiWaveType.Rank;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Time.currentMillis();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const minStartTimestamp = t1;
  const minVotingTimestamp = isRankWave ? Math.max(dates.submissionStartDate, minStartTimestamp) : null;
  let t2;
  if ($[1] !== dates || $[2] !== isRankWave || $[3] !== setDates) {
    t2 = timestamp => {
      const adjustedTimestamp = Math.max(timestamp, Time.currentMillis());
      setDates({
        ...dates,
        submissionStartDate: adjustedTimestamp,
        votingStartDate: isRankWave ? Math.max(dates.votingStartDate, adjustedTimestamp) : adjustedTimestamp
      });
    };
    $[1] = dates;
    $[2] = isRankWave;
    $[3] = setDates;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleSubmissionDateChange = t2;
  let t3;
  if ($[5] !== dates || $[6] !== setDates) {
    t3 = timestamp_0 => {
      setDates({
        ...dates,
        votingStartDate: Math.max(timestamp_0, dates.submissionStartDate)
      });
    };
    $[5] = dates;
    $[6] = setDates;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleVotingDateChange = t3;
  const formatShortDate = _temp;
  let t4;
  if ($[8] !== dates.submissionStartDate) {
    t4 = formatShortDate(dates.submissionStartDate);
    $[8] = dates.submissionStartDate;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const submissionDateFormatted = t4;
  let t5;
  if ($[10] !== dates.votingStartDate) {
    t5 = formatShortDate(dates.votingStartDate);
    $[10] = dates.votingStartDate;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const votingDateFormatted = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <TooltipIconButton icon={faInfoCircle} tooltipText="Set when your wave begins accepting submissions and when voting starts. These dates create the foundational timeline for your wave." tooltipPosition="bottom" tooltipWidth="tw-w-80" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== isExpanded || $[14] !== setIsExpanded) {
    t7 = () => setIsExpanded(!isExpanded);
    $[13] = isExpanded;
    $[14] = setIsExpanded;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <FontAwesomeIcon icon={faCalendarDays} className="tw-mr-2 tw-size-4 tw-text-primary-400" />;
    t9 = <p className="tw-mb-0 tw-text-xs tw-text-iron-300/70">Drops Submission Opens</p>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  let t10;
  if ($[18] !== submissionDateFormatted) {
    t10 = <div className="tw-flex tw-items-center tw-rounded-lg tw-bg-iron-700/40 tw-px-3 tw-py-2 tw-shadow-md tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]">{t8}<div>{t9}<p className="tw-mb-0 tw-text-sm tw-font-medium tw-text-iron-50">{submissionDateFormatted}</p></div></div>;
    $[18] = submissionDateFormatted;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== isRankWave || $[21] !== votingDateFormatted) {
    t11 = isRankWave && <div className="tw-flex tw-items-center tw-rounded-lg tw-bg-iron-700/40 tw-px-3 tw-py-2 tw-shadow-md tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"><FontAwesomeIcon icon={faCalendarDays} className="tw-mr-2 tw-size-4 tw-text-primary-400" /><div><p className="tw-mb-0 tw-text-xs tw-text-iron-300/70">Drops Voting Begins</p><p className="tw-mb-0 tw-text-sm tw-font-medium tw-text-iron-50">{votingDateFormatted}</p></div></div>;
    $[20] = isRankWave;
    $[21] = votingDateFormatted;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = <div className="tw-flex tw-items-center tw-space-x-4">{t10}{t11}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = CREATE_WAVE_START_DATE_LABELS[waveType];
  let t14;
  if ($[26] !== t13) {
    t14 = <p className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-50 sm:tw-text-xl">{t13}</p>;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <p className="tw-mt-1 tw-text-xs tw-text-iron-400">Creators begin submitting work to your wave</p>;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = new Date().getMonth();
    t17 = new Date().getFullYear();
    $[29] = t16;
    $[30] = t17;
  } else {
    t16 = $[29];
    t17 = $[30];
  }
  let t18;
  if ($[31] !== dates.submissionStartDate || $[32] !== handleSubmissionDateChange) {
    t18 = <div className="tw-mt-4"><CommonCalendar initialMonth={t16} initialYear={t17} selectedTimestamp={dates.submissionStartDate} minTimestamp={minStartTimestamp} maxTimestamp={null} setSelectedTimestamp={handleSubmissionDateChange} /></div>;
    $[31] = dates.submissionStartDate;
    $[32] = handleSubmissionDateChange;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t14 || $[35] !== t18) {
    t19 = <div className="tw-col-span-1">{t14}{t15}{t18}</div>;
    $[34] = t14;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== dates.votingStartDate || $[38] !== handleVotingDateChange || $[39] !== isRankWave || $[40] !== minVotingTimestamp) {
    t20 = isRankWave && <div className="tw-col-span-1"><p className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-50 sm:tw-text-xl">Drops Voting Begins</p><p className="tw-mt-1 tw-text-xs tw-text-iron-400">Community voting on wave submissions begins</p><div className="tw-mt-4"><CommonCalendar initialMonth={new Date().getMonth()} initialYear={new Date().getFullYear()} selectedTimestamp={dates.votingStartDate} minTimestamp={minVotingTimestamp} maxTimestamp={null} setSelectedTimestamp={handleVotingDateChange} /></div></div>;
    $[37] = dates.votingStartDate;
    $[38] = handleVotingDateChange;
    $[39] = isRankWave;
    $[40] = minVotingTimestamp;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t19 || $[43] !== t20) {
    t21 = <div className="tw-grid tw-grid-cols-1 tw-gap-x-10 tw-gap-y-8 tw-px-5 tw-pb-5 tw-pt-2 md:tw-grid-cols-2">{t19}{t20}</div>;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== isExpanded || $[46] !== t12 || $[47] !== t21 || $[48] !== t7) {
    t22 = <DateAccordion title="Wave Timeline" titleActions={t6} isExpanded={isExpanded} onToggle={t7} collapsedContent={t12}>{t21}</DateAccordion>;
    $[45] = isExpanded;
    $[46] = t12;
    $[47] = t21;
    $[48] = t7;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  return t22;
}
function _temp(timestamp_1) {
  return new Date(timestamp_1).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}